/**
 * Responsibility: the controls that narrow the collection.
 *
 * Behind a button, but never silently. Two rows of controls above the grid
 * cost more vertical space than the grid could spare, so they live in a
 * popover - and the risk of that is the one thing this has to defend
 * against: a filter left on last week, hidden, making the page look empty
 * for no reason anybody can see. So the button carries the number of
 * active filters and turns primary, the count of hidden holdings sits
 * beside it, and both are visible without opening anything.
 *
 * The menus offer only what the collection actually contains. Listing every
 * grading company in existence against three owned cards makes the operator
 * hunt for the one that applies, and a value nothing matches cannot narrow
 * anything.
 *
 * Two rows inside, split by what they do: the first picks a bucket, the
 * second bounds a number or a date. Ranges are left open-ended on purpose -
 * one end of a bound is usually the whole question ("anything over $500").
 */
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/FilterAltOffOutlined';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';
import { useState } from 'react';
import type { CollectionTag } from '../../api/client';
import type { CollectionFilters } from './collectionFilters';
import { LANGUAGES, NO_FILTERS, activeCount, isUnfiltered } from './collectionFilters';

type Options = {
  companies: string[];
  sets: string[];
  languages: string[];
};

type CollectionFilterBarProps = {
  filters: CollectionFilters;
  onChange: (next: CollectionFilters) => void;
  options: Options;
  /** The sub-collections that exist, for the grouping menu. */
  tags: CollectionTag[];
  /** How many holdings the filters are currently hiding. */
  hidden: number;
};

/** Narrow enough that a row of them fits, wide enough to read a set name. */
const SELECT_WIDTH = 150;
const NUMBER_WIDTH = 104;
const DATE_WIDTH = 158;

export function CollectionFilterBar({
  filters,
  onChange,
  options,
  tags,
  hidden,
}: CollectionFilterBarProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const set = <K extends keyof CollectionFilters>(key: K, value: CollectionFilters[K]) =>
    onChange({ ...filters, [key]: value });

  const clean = isUnfiltered(filters);
  const active = activeCount(filters);

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <Badge badgeContent={active} color="primary">
          <Button
            size="small"
            variant={clean ? 'outlined' : 'contained'}
            startIcon={<FilterIcon />}
            onClick={(event) => setAnchor(event.currentTarget)}
          >
            Filters
          </Button>
        </Badge>

        {/* Said out loud and outside the popover, because a filtered shelf
            and a small collection look identical otherwise. */}
        {hidden > 0 ? (
          <Typography variant="caption" color="text.secondary">
            {hidden} hidden
          </Typography>
        ) : null}
      </Stack>

      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 720 } } }}
      >
    <Stack spacing={1} sx={{ flexShrink: 0 }}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filters.slab}
          onChange={(_event, next: CollectionFilters['slab'] | null) =>
            set('slab', next ?? '')
          }
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="RAW">Raw</ToggleButton>
          <ToggleButton value="GRADED">Graded</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          select
          size="small"
          label="Card type"
          value={filters.category}
          onChange={(event) => set('category', event.target.value)}
          sx={{ minWidth: SELECT_WIDTH }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="TCG">Trading cards</MenuItem>
          <MenuItem value="SPORTS">Sports</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Graded by"
          value={filters.company}
          onChange={(event) => set('company', event.target.value)}
          sx={{ minWidth: SELECT_WIDTH }}
          disabled={options.companies.length === 0}
        >
          <MenuItem value="">Any</MenuItem>
          {options.companies.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Sub-collection"
          value={filters.tag}
          onChange={(event) => set('tag', event.target.value)}
          sx={{ minWidth: SELECT_WIDTH, maxWidth: 260 }}
        >
          <MenuItem value="">Any</MenuItem>
          {tags.map((tag) => (
            // The id is the value: a tag renamed while this filter is saved
            // must keep filtering the same cards.
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
              {tag.goal ? ` (${tag.held}/${tag.goal})` : ` (${tag.held})`}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Set"
          value={filters.set}
          onChange={(event) => set('set', event.target.value)}
          sx={{ minWidth: SELECT_WIDTH, maxWidth: 260 }}
        >
          <MenuItem value="">Any</MenuItem>
          {options.sets.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Language"
          value={filters.language}
          onChange={(event) => set('language', event.target.value)}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Any</MenuItem>
          {options.languages.map((code) => (
            <MenuItem key={code} value={code}>
              {LANGUAGES[code] ?? code}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Grade from"
            value={filters.minGrade}
            onChange={(event) =>
              set('minGrade', event.target.value.replace(/[^0-9.]/g, ''))
            }
            sx={{ width: NUMBER_WIDTH }}
          />
          <TextField
            size="small"
            label="to"
            value={filters.maxGrade}
            onChange={(event) =>
              set('maxGrade', event.target.value.replace(/[^0-9.]/g, ''))
            }
            sx={{ width: NUMBER_WIDTH }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Value from"
            value={filters.minValue}
            onChange={(event) =>
              set('minValue', event.target.value.replace(/[^0-9.]/g, ''))
            }
            sx={{ width: NUMBER_WIDTH }}
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
            }}
          />
          <TextField
            size="small"
            label="to"
            value={filters.maxValue}
            onChange={(event) =>
              set('maxValue', event.target.value.replace(/[^0-9.]/g, ''))
            }
            sx={{ width: NUMBER_WIDTH }}
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="date"
            label="Added from"
            value={filters.addedFrom}
            onChange={(event) => set('addedFrom', event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: DATE_WIDTH }}
          />
          <TextField
            size="small"
            type="date"
            label="Added to"
            value={filters.addedTo}
            onChange={(event) => set('addedTo', event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: DATE_WIDTH }}
          />
        </Stack>

        {!clean ? (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={() => onChange(NO_FILTERS)}
            sx={{ flexShrink: 0 }}
          >
            Clear
          </Button>
        ) : null}

        {hidden > 0 ? (
          <Typography variant="caption" color="text.secondary">
            {hidden} hidden by filters
          </Typography>
        ) : null}
      </Stack>
    </Stack>
      </Popover>
    </>
  );
}
